import { Static, Type } from '@sinclair/typebox';
import { ProviderSchema } from '../../common/provider.schema';
import { SocialHandlesSchema } from '../../shared/social-handle.schema';
import { minimalUserSchema } from '../../shared/user/user.schema';
import { StringEnum } from '../../utils/typebox';

export const SocialHandleProviderSchema = StringEnum([
  'linkedin',
  'instagram',
  'github',
  'facebook',
  'x',
  'threads',
  'tiktok',
  'wechat',
  'gtalk',
  'qq',
  'skype',
]);

export type SocialHandleProvider = Static<typeof SocialHandleProviderSchema>;

export const userProfileBaseSchema = Type.Object({
  ...minimalUserSchema,
  object: Type.Literal('UserProfile'),
  background_picture_url: Type.Optional(Type.String({ description: "URL of the user's background / banner picture." })),
  public_picture_url_large: Type.Optional(Type.String({ description: "URL of the user's public picture in large size." })),
  birth_date: Type.Optional(Type.String({ description: 'Birth date of the user.' })),
  addresses: Type.Optional(Type.Array(Type.String(), { description: 'List of user addresses.' })),
  emails: Type.Optional(Type.Array(Type.String(), { description: 'List of user email addresses.' })),
  phone_numbers: Type.Optional(Type.Array(Type.String(), { description: 'List of user phone numbers.' })),
  created_at: Type.Optional(Type.String({ description: 'Date and time when the user account was created on the provider.' })),
  bio: Type.Optional(Type.String({ description: 'Biography of the user.' })),
  location: Type.Optional(Type.String({ description: 'Location of the user.' })),
  muted_until: Type.Optional(Type.String({ description: 'Date and time until when the user is muted. ' })),
  is_following: Type.Optional(Type.Boolean({ description: 'Whether the user is followed by the current user.' })),
  followers_count: Type.Optional(Type.Number({ description: 'Number of followers of the user.' })),
  following_count: Type.Optional(Type.Number({ description: 'Number of users followed by the user.' })),
  relations_count: Type.Optional(Type.Number({ description: 'Number of relations of the user.' })),
  shared_relations_count: Type.Optional(
    Type.Number({ description: 'Number of users that have a relation with the given profile and with the account owner.' }),
  ),
  shared_followers_count: Type.Optional(
    Type.Number({ description: 'Number of following users that follows the given profile.' }),
  ),
  is_blocked: Type.Boolean({ description: 'Whether the user is blocked by the current user.' }),
  is_contact: Type.Optional(
    Type.Boolean({
      description: 'Whether the user is saved in the address book of the account owner (if supported by the provider).',
    }),
  ),
  is_verified: Type.Optional(Type.Boolean({ description: 'Whether the user is verified by the provider.' })),
  is_premium: Type.Optional(Type.Boolean({ description: 'Whether the user has a premium subscription with the provider.' })),
  is_deleted: Type.Optional(Type.Boolean({ description: 'Whether the user account was deleted or deactivated.' })),
  is_online: Type.Optional(Type.Boolean({ description: 'Whether the user is currently online (if supported by the provider).' })),
  last_online_at: Type.Optional(
    Type.String({ description: 'Date and time when the user was last online (if supported by the provider).' }),
  ),
  language: Type.Optional(Type.String({ description: 'Language of the user, as a lowercase ISO 639-1 code (e.g. `fr`).' })),
  provider: ProviderSchema,
  specifics: Type.Any(),
  social_handles: Type.Optional(SocialHandlesSchema),
  websites: Type.Optional(Type.Array(Type.String(), { description: 'List of user websites.' })),
});

export type ProviderUserProfile<Specifics = unknown> = Omit<Static<typeof userProfileBaseSchema>, 'specifics'> & {
  specifics: Specifics;
};
